import type { AccountMenuItem as AccountMenuItemData } from './accountMenuData';
import type { ReactElement } from 'react';

import { MinusIcon, PlusIcon } from '@phosphor-icons/react';
import { memo } from 'react';

import { S } from './accountMenuStyles';
import { getNavItemPath } from '~/utilities/helpers/getNavItemPath';
import { useTopNavTracking } from '~/utilities/hooks/useTopNavTracking';

type AccountMenuItemProps = {
  item: AccountMenuItemData;
  isExpanded: boolean;
  onToggle: (itemKey: string) => void;
  onClose: () => void;
};

const AccountMenuItemComponent = ({
  item,
  isExpanded,
  onToggle,
  onClose,
}: AccountMenuItemProps): ReactElement => {
  const itemKey = item.uid || item.label;
  const { track } = useTopNavTracking();

  if (item.children && item.children.length > 0) {
    const itemSlug = itemKey.replace(/\s+/g, '-').toLowerCase();
    const buttonId = `account-menu-accordion-button-${itemSlug}`;
    const subMenuId = `account-menu-sublinks-${itemSlug}`;

    return (
      <S.MenuItem>
        <S.AccordionButton
          id={buttonId}
          type="button"
          aria-expanded={isExpanded}
          aria-controls={subMenuId}
          onClick={() => {
            track({
              event: 'account_interaction',
              nav_item_label: item.label,
              nav_item_path: getNavItemPath([item.label]),
              nav_level: 2,
              interaction_type: isExpanded ? 'close' : 'open',
            });
            onToggle(itemKey);
          }}
          data-testid="account-menu-accordion-toggle"
        >
          <span>{item.label}</span>
          <S.AccordionIcon aria-hidden="true" data-testid="account-menu-accordion-icon">
            {isExpanded ? (
              <MinusIcon size={12} data-testid="accordion-minus-icon" />
            ) : (
              <PlusIcon size={12} data-testid="accordion-plus-icon" />
            )}
          </S.AccordionIcon>
        </S.AccordionButton>

        {isExpanded && (
          <S.SubMenuList
            id={subMenuId}
            aria-labelledby={buttonId}
            data-testid="account-menu-sublinks"
          >
            {item.children.map(subItem => (
              <S.SubMenuItem key={subItem.uid || subItem.url}>
                <S.SubMenuLink
                  href={subItem.url}
                  onClick={() => {
                    track({
                      event: 'account_interaction',
                      nav_item_label: subItem.label,
                      nav_item_path: getNavItemPath([item.label, subItem.label]),
                      nav_level: 3,
                      interaction_type: 'click',
                    });
                    onClose();
                  }}
                  data-testid="account-menu-link"
                >
                  {subItem.label}
                </S.SubMenuLink>
              </S.SubMenuItem>
            ))}
          </S.SubMenuList>
        )}
      </S.MenuItem>
    );
  }

  return (
    <S.MenuItem>
      <S.MenuLink
        href={item.url ?? ''}
        onClick={() => {
          track({
            event: 'account_interaction',
            nav_item_label: item.label,
            nav_item_path: getNavItemPath([item.label]),
            nav_level: 2,
            interaction_type: 'click',
          });
          onClose();
        }}
        data-testid="account-menu-link"
      >
        {item.label}
      </S.MenuLink>
    </S.MenuItem>
  );
};

export const AccountMenuItem = memo(AccountMenuItemComponent);
